import { supabase } from "@/integrations/supabase/client";
import { log } from "@/lib/log";

export interface AuditEventOptions {
  entity_type?: string;
  entity_id?: string;
  organization_id?: string;
  details?: Record<string, unknown>;
}

/**
 * Fire-and-forget audit event logger. Never blocks the UI on logging failure.
 * Captures IP + User-Agent server-side via the audit-log edge function.
 */
export function logAuditEvent(action: string, opts: AuditEventOptions = {}): void {
  void supabase.functions
    .invoke("audit-log", { body: { action, ...opts } })
    .catch((err) => {
      log.warn("audit.log_failed", {
        action,
        entity_type: opts.entity_type,
        entity_id: opts.entity_id,
        organization_id: opts.organization_id,
        error: err instanceof Error ? err.message : String(err),
      });
    });
}

/**
 * Auth-event logger. Works without an active session (failures, signups).
 */
export function logAuthEvent(
  event: string,
  opts: { email?: string; details?: Record<string, unknown> } = {}
): void {
  void supabase.functions.invoke("log-auth-event", { body: { event, ...opts } }).catch((err) => {
    log.warn("auth_audit.log_failed", {
      event,
      error: err instanceof Error ? err.message : String(err),
    });
  });
}

/** SHA-256 hex digest of a string using Web Crypto. */
export async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
